import { memo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";
import type { TextRenderBlock } from "@/lib/chat-render";
import { Avatar } from "@/components/common/Avatar";
import { CopyIconButton } from "@/components/common/CopyIconButton";
import { IconButton } from "@/components/common/IconButton";
import { StatusDot } from "@/components/common/StatusDot";
import { formatFullName } from "@/lib/format-name";
import { selectIsChatEnvironmentStopped, useChatStore } from "@/stores/useChatStore";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";
import { useOrgUsersStore } from "@/stores/useOrgUsersStore";
import { AttachmentBlock } from "./AttachmentBlock";
import { chatMarkdownComponents, compactChatMarkdownComponents } from "./chat-markdown-components";
import { MessageDocuments } from "./MessageDocuments";
import { MessageImages } from "./MessageImages";
import { RelativeTimestamp } from "./RelativeTimestamp";
import { useBranchChat } from "./useBranchChat";

interface MessageProps {
	message: TextRenderBlock;
	isLastInTurn?: boolean;
	compact?: boolean;
}

/** 他ユーザーのメッセージに表示するアバターの直径と、バブル側の左マージン。 */
const AVATAR_SIZE = 28;
const AVATAR_GAP = 8;

const REMARK_PLUGINS = [remarkGfm];

/**
 * ポーリング等で履歴全体が再描画されても、本文が変わらない限り Markdown を再パースしない。
 * 長い履歴では全メッセージの再パースが入力欄を固まらせるほど重い。
 */
export const MessageMarkdown = memo(function MessageMarkdown({
	text,
	components,
}: {
	text: string;
	components: Components;
}) {
	return (
		<ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={components}>
			{text}
		</ReactMarkdown>
	);
});

/** このユーザーメッセージの直前までを複製した新しいチャットを開くボタン。 */
function BranchIconButton({ message }: { message: TextRenderBlock }) {
	const branch = useBranchChat();
	const [busy, setBusy] = useState(false);
	const handleClick = async () => {
		setBusy(true);
		try {
			await branch(message.messageId, {
				text: message.text,
				images: message.images,
				documents: message.documents,
				uploadedFiles: message.uploadedFiles,
			});
		} finally {
			setBusy(false);
		}
	};
	return (
		<IconButton
			icon="branch"
			iconSize={14}
			size={20}
			color="var(--fg-3)"
			hoverBackground="transparent"
			activeBackground="transparent"
			disabled={busy}
			onClick={handleClick}
			title="ここから会話を分岐"
			data-testid="message-branch"
		/>
	);
}

function MessageFooter({
	message,
	align,
	canBranch = false,
}: {
	message: TextRenderBlock;
	align: "left" | "right";
	/** 永続化済みユーザーメッセージのみ true。送信中の楽観行や別画面の埋め込み表示では出さない。 */
	canBranch?: boolean;
}) {
	const hasText = message.text.length > 0;
	const isUser = message.role === "user";
	return (
		<div
			className={`opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex items-center gap-2 mt-1 ${align === "right" ? "justify-end" : "justify-start"}`}
		>
			{message.createdAt && (
				<RelativeTimestamp
					iso={message.createdAt}
					style={{
						fontSize: "var(--text-section)",
						color: "var(--fg-3)",
					}}
				/>
			)}
			{isUser && (
				<StatusDot
					tone={message.sendStatus === "sending" ? "info" : "muted"}
					pulse={message.sendStatus === "sending"}
					tooltip={
						message.sendStatus === "sending"
							? "送信中: メッセージをエージェントに転送しています。"
							: "送信済み: メッセージはエージェントに届きました。"
					}
					testId="user-message-status"
					state={message.sendStatus}
				/>
			)}
			{hasText && <CopyIconButton value={message.text} label="内容をコピー" />}
			{canBranch && <BranchIconButton message={message} />}
		</div>
	);
}

function SystemErrorMessage({ message }: MessageProps) {
	return (
		<div data-testid="message-systemError" data-role="systemError" style={{ padding: "8px 0" }}>
			<div
				className="select-text"
				style={{
					display: "flex",
					gap: 8,
					borderRadius: "var(--radius-4)",
					border: "1px solid var(--danger-line)",
					background: "var(--danger-subtle)",
					padding: "10px 12px",
					fontSize: "var(--text-ui)",
					lineHeight: "var(--leading-body)",
					color: "var(--danger)",
				}}
			>
				<span style={{ flexShrink: 0 }}>⚠</span>
				<p className="whitespace-pre-wrap wrap-anywhere my-0">{message.text}</p>
			</div>
		</div>
	);
}

export function ChatMessage({ message, isLastInTurn = true, compact = false }: MessageProps) {
	const currentUserId = useCurrentUserStore((s) => s.id);
	const sender = useOrgUsersStore((s) =>
		message.createdById ? s.usersById[message.createdById] : undefined,
	);
	// 分岐先は同じ環境で続けるため、環境が起動していない間は送信と同様に分岐も出さない。
	const environmentStopped = useChatStore(selectIsChatEnvironmentStopped);

	if (message.role === "systemError") {
		return <SystemErrorMessage message={message} />;
	}
	// システム・バックグラウンドイベントは射影で除外されるが、型の網羅性のため防御的に無視する。
	if (
		message.role === "systemNotice" ||
		message.role === "monitorEvent" ||
		message.role === "compaction" ||
		message.role === "subagentEvent"
	) {
		return null;
	}

	const isUser = message.role === "user";
	const isOwnMessage = message.createdById === currentUserId;
	const canBranch = isUser && !compact && message.sendStatus === "sent" && !environmentStopped;
	const { text, controls } = message;

	if (isUser && !isOwnMessage) {
		const senderName = sender ? formatFullName(sender) : "ユーザー";
		return (
			<div
				data-testid="message-user"
				data-role="user"
				className="group"
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "flex-start",
					paddingTop: 8,
					paddingBottom: isLastInTurn ? 8 : 0,
				}}
			>
				<div
					style={{ display: "flex", alignItems: "flex-end", gap: AVATAR_GAP, alignSelf: "stretch" }}
				>
					<Avatar
						size={AVATAR_SIZE}
						firstName={sender?.firstName ?? ""}
						lastName={sender?.lastName ?? ""}
					/>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "flex-start",
							flex: "1 1 auto",
							minWidth: 0,
						}}
					>
						<span
							style={{ fontSize: "var(--text-caption)", color: "var(--fg-3)", marginBottom: 4 }}
						>
							{senderName}
						</span>
						<div
							data-message-body
							className="select-text"
							style={{
								background: "var(--surface-2)",
								borderRadius: "var(--radius-6)",
								padding: "8px 14px",
								maxWidth: "75%",
								fontSize: "var(--text-ui)",
								lineHeight: "var(--leading-body)",
								color: "var(--fg-1)",
							}}
						>
							<MessageImages images={message.images} />
							<MessageDocuments
								documents={message.documents}
								uploadedFiles={message.uploadedFiles}
							/>
							{text.length > 0 && <p className="whitespace-pre-wrap wrap-anywhere my-0">{text}</p>}
							{controls.map((control, idx) => (
								<div key={idx}>
									<AttachmentBlock control={control} />
								</div>
							))}
						</div>
					</div>
				</div>
				<div style={{ marginLeft: AVATAR_SIZE + AVATAR_GAP }}>
					<MessageFooter message={message} align="left" canBranch={canBranch} />
				</div>
			</div>
		);
	}

	if (isUser) {
		return (
			<div
				data-testid="message-user"
				data-role="user"
				className="group"
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "flex-end",
					paddingTop: 8,
					paddingBottom: isLastInTurn ? 8 : 0,
				}}
			>
				<div
					data-message-body
					className="select-text"
					style={{
						background: "var(--surface-2)",
						borderRadius: "var(--radius-6)",
						padding: "8px 14px",
						maxWidth: "75%",
						fontSize: "var(--text-ui)",
						lineHeight: "var(--leading-body)",
						color: "var(--fg-1)",
					}}
				>
					<MessageImages images={message.images} />
					<MessageDocuments documents={message.documents} uploadedFiles={message.uploadedFiles} />
					{text.length > 0 && <p className="whitespace-pre-wrap wrap-anywhere my-0">{text}</p>}
					{controls.map((control, idx) => (
						<div key={idx}>
							<AttachmentBlock control={control} />
						</div>
					))}
				</div>
				<MessageFooter message={message} align="right" canBranch={canBranch} />
			</div>
		);
	}

	const hasText = text.length > 0;

	return (
		<div
			data-testid={`message-${message.role}`}
			data-role={message.role}
			className="group"
			style={{
				paddingTop: 8,
				paddingBottom: isLastInTurn ? 8 : 0,
			}}
		>
			<div
				data-message-body
				className="select-text"
				style={{
					fontSize: compact ? "var(--text-caption)" : "var(--text-ui)",
					lineHeight: "var(--leading-body)",
					color: "var(--fg-1)",
				}}
			>
				<MessageImages images={message.images} />
				<MessageDocuments documents={message.documents} uploadedFiles={message.uploadedFiles} />
				{hasText && (
					<MessageMarkdown
						text={text}
						components={compact ? compactChatMarkdownComponents : chatMarkdownComponents}
					/>
				)}
				{controls.map((control, idx) => (
					<div key={idx}>
						<AttachmentBlock control={control} />
					</div>
				))}
			</div>
			{isLastInTurn && <MessageFooter message={message} align="left" />}
		</div>
	);
}
